import Button from "@mui/material/Button/Button";
import Grid from "@mui/material/Grid2";
import TextField from "@mui/material/TextField/TextField";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { CepTextMask } from "../../../components/Masks/CepTextMask";
import { CnpjTextMask } from "../../../components/Masks/CnpjTextMask";
import { PhoneTextMask } from "../../../components/Masks/PhoneTextMask";
import { createCompany } from "../../../services/api/companies";
import { ICompany } from "../../../types/Company.types";
import { sanitizeMaskedString } from "../../../utils/helpers/parsers";
import { createCompanyEmptyForm, ICreateCompanyForm } from "./forms";

interface ICompanySignUpForm {
	nextStepHandler: any;
	companySetter: any;
}

function CompanySignUpForm(props: ICompanySignUpForm) {
	function FormFields() {
		const { control, handleSubmit } = useForm<ICreateCompanyForm>();

		const onSubmit: SubmitHandler<ICreateCompanyForm> = async (
			formData: ICreateCompanyForm
		) => {
			const company = {
				name: formData.name,
				taxId: sanitizeMaskedString(formData.taxId),
				phone: sanitizeMaskedString(formData.phone),
				email: formData.email,
				address: {
					street: formData.address_street,
					number: formData.address_number,
					complement: formData.address_complement,
					neighborhood: formData.address_neighborhood,
					city: formData.address_city,
					state: formData.address_state,
					zipCode: sanitizeMaskedString(formData.address_zipCode),
				},
				status: "draft",
				plan: "free_trial",
			} as ICompany;
			try {
				props.companySetter(company.taxId);
				props.nextStepHandler();
				await createCompany(company);
			} catch (error: any) {
				console.error(error);
			}
		};

		return (
			<>
				<form id="companySignUp" onSubmit={handleSubmit(onSubmit)}>
					<Grid container spacing={3}>
						<Grid size={{ xs: 12 }}>
							<Controller
								name="name"
								control={control}
								defaultValue={createCompanyEmptyForm.name}
								render={({ field: { onChange, value } }) => (
									<TextField
										name="name"
										required
										variant="standard"
										fullWidth
										id="name"
										label="Nome da Empresa"
										value={value}
										onChange={onChange}
									/>
								)}
							/>
						</Grid>
						<Grid size={{ xs: 12 }}>
							<Controller
								name="taxId"
								control={control}
								defaultValue={createCompanyEmptyForm.taxId}
								render={({ field: { onChange, value } }) => (
									<TextField
										required
										variant="standard"
										fullWidth
										value={value}
										id="taxId"
										label="CNPJ da Empresa"
										name="taxId"
										type="tel"
										onChange={onChange}
										slotProps={{
											input: {
												inputComponent:
													CnpjTextMask as any,
											},
										}}
									/>
								)}
							/>
						</Grid>
						<Grid size={{ xs: 12 }}>
							<Controller
								name="phone"
								control={control}
								defaultValue={createCompanyEmptyForm.phone}
								render={({ field: { onChange, value } }) => (
									<TextField
										required
										variant="standard"
										fullWidth
										type="tel"
										id="phone"
										value={value}
										label="Telefone"
										name="phone"
										onChange={onChange}
										slotProps={{
											input: {
												inputComponent:
													PhoneTextMask as any,
											},
										}}
									/>
								)}
							/>
						</Grid>
						<Grid size={{ xs: 12 }}>
							<Controller
								name="email"
								control={control}
								defaultValue={createCompanyEmptyForm.email}
								render={({ field: { onChange, value } }) => (
									<TextField
										required
										variant="standard"
										fullWidth
										value={value}
										id="email"
										type="email"
										label="Email"
										name="email"
										onChange={onChange}
									/>
								)}
							/>
						</Grid>
						<Grid size={{ xs: 12 }}>
							<Controller
								name="address_street"
								control={control}
								defaultValue={
									createCompanyEmptyForm.address_street
								}
								render={({ field: { onChange, value } }) => (
									<TextField
										required
										value={value}
										variant="standard"
										fullWidth
										id="address_street"
										label="Endereço"
										onChange={onChange}
										name="address_street"
									/>
								)}
							/>
						</Grid>
						<Grid size={{ xs: 4 }}>
							<Controller
								name="address_number"
								control={control}
								defaultValue={
									createCompanyEmptyForm.address_number
								}
								render={({ field: { onChange, value } }) => (
									<TextField
										required
										variant="standard"
										value={value}
										fullWidth
										id="address_number"
										label="Nº"
										onChange={onChange}
										name="address_number"
									/>
								)}
							/>
						</Grid>
						<Grid size={{ xs: 4 }}>
							<Controller
								name="address_complement"
								control={control}
								defaultValue={
									createCompanyEmptyForm.address_complement
								}
								render={({ field: { onChange, value } }) => (
									<TextField
										variant="standard"
										fullWidth
										value={value}
										id="address_complement"
										label="Complemento"
										onChange={onChange}
										name="address_complement"
									/>
								)}
							/>
						</Grid>
						<Grid size={{ xs: 4 }}>
							<Controller
								name="address_neighborhood"
								control={control}
								defaultValue={
									createCompanyEmptyForm.address_neighborhood
								}
								render={({ field: { onChange, value } }) => (
									<TextField
										required
										variant="standard"
										value={value}
										fullWidth
										id="address_neighborhood"
										label="Bairro"
										onChange={onChange}
										name="address_neighborhood"
									/>
								)}
							/>
						</Grid>
						<Grid size={{ xs: 6 }}>
							<Controller
								name="address_city"
								control={control}
								defaultValue={
									createCompanyEmptyForm.address_city
								}
								render={({ field: { onChange, value } }) => (
									<TextField
										required
										variant="standard"
										fullWidth
										id="address_city"
										label="Cidade"
										onChange={onChange}
										value={value}
										name="address_city"
									/>
								)}
							/>
						</Grid>
						<Grid size={{ xs: 6 }}>
							<Controller
								name="address_state"
								control={control}
								defaultValue={
									createCompanyEmptyForm.address_state
								}
								render={({ field: { onChange, value } }) => (
									<TextField
										required
										onChange={onChange}
										variant="standard"
										fullWidth
										id="address_state"
										value={value}
										label="Estado"
										name="address_state"
									/>
								)}
							/>
						</Grid>
						<Grid size={{ xs: 12 }}>
							<Controller
								name="address_zipCode"
								control={control}
								defaultValue={
									createCompanyEmptyForm.address_zipCode
								}
								render={({ field: { onChange, value } }) => (
									<TextField
										required
										variant="standard"
										onChange={onChange}
										fullWidth
										type="tel"
										value={value}
										id="address_zipCode"
										label="CEP"
										name="address_zipCode"
										slotProps={{
											input: {
												inputComponent:
													CepTextMask as any,
											},
										}}
									/>
								)}
							/>
						</Grid>
					</Grid>
					<Button
						type="submit"
						fullWidth
						variant="contained"
						color="primary"
						style={{ marginTop: "16px" }}
					>
						CADASTRAR
					</Button>
				</form>
			</>
		);
	}

	return (
		<div>
			<Grid size={{ xs: 12 }}>
				<FormFields />
			</Grid>
		</div>
	);
}

export { CompanySignUpForm };
